import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { JobProcess } from '../../model/job-process.model';
import { environment } from '../../../environments/environment';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

interface AddJobProcessResponse {
  data?: string;
  jobProcessId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class JobprocessService {
  private apiUrl = environment.apiUrl + '/kbj-frontend/api/v1/Master/JobProcess';
  private readonly httpOptions = { withCredentials: true };
  private http: HttpClient = inject(HttpClient);

  /**
   * Returns the job processes for the current context.
   * Uses withCredentials so cookies from a different subdomain are included.
   * Returns an empty array when the API responds with 204 No Content.
   */
  getByContext(take: number, status: string): Observable<JobProcess[]> {
    const params = new HttpParams().set('take', take).set('status', status ?? '');

    return this.http
      .get<JobProcess[] | null>(this.apiUrl + '/ByContext', { ...this.httpOptions, params })
      .pipe(
        map((jsonData) => jsonData ?? []),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of([]);
          }
          return this.handleError(err);
        })
      );
  }

  /**
   * Returns a single job process by its id.
   * Uses withCredentials so cookies from a different subdomain are included.
   */
  getById(id: string): Observable<JobProcess | null> {
    return this.http
      .get<JobProcess | null>(this.apiUrl + '/' + id, this.httpOptions)
      .pipe(
        map((jsonData) => jsonData ?? null),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of(null);
          }
          return this.handleError(err);
        })
      );
  }

  /**
   * Returns the selectable job names used to populate the "execCmd" dropdown.
   * Returns an empty array when the API responds with 404 or 204 No Content.
   */
  getJobNames(): Observable<string[]> {
    return this.http
      .get<string[] | null>(this.apiUrl + '/JobNames', this.httpOptions)
      .pipe(
        map((jsonData) => jsonData ?? []),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of([]);
          }
          return this.handleError(err);
        })
      );
  }

  /**
   * Creates a new job process. The job process id is always forced to the empty
   * guid so the backend can generate a new one. Only the fields the backend
   * accepts on create are sent.
   */
  addJobProcess(job: JobProcess): Observable<JobProcess> {
    const payload = {
      jobProcessId: EMPTY_GUID,
      websiteInstanceId: job.websiteInstanceId ?? null,
      name: job.name,
      description: job.description ?? null,
      tenantCode: job.tenantCode ?? null,
      parameters: job.parameters ?? null,
      execCmd: job.execCmd ?? null,
      status: job.status ?? null,
      notRestartable: job.notRestartable,
      errorMessage: job.errorMessage ?? null,
      lastRunned: job.lastRunned ?? null,
      result: job.result ?? null,
    };

    return this.http
      .post<AddJobProcessResponse | null>(this.apiUrl, payload, this.httpOptions)
      .pipe(
        map((response) => ({ ...job, jobProcessId: this.getCreatedJobProcessId(response) })),
        catchError((err: HttpErrorResponse) => this.handleError(err))
      );
  }

  /**
   * Updates an existing job process.
   */
  updateJobProcess(id: string, job: JobProcess): Observable<JobProcess> {
    const payload: JobProcess = { ...job, jobProcessId: id };

    return this.http
      .put<unknown>(this.apiUrl + '/' + id, payload, this.httpOptions)
      .pipe(
        map(() => ({ ...job, jobProcessId: id })),
        catchError((err: HttpErrorResponse) => this.handleError(err))
      );
  }

  /**
   * Removes a job process by its id.
   */
  removeJobProcess(id: string): Observable<unknown> {
    return this.http
      .delete(this.apiUrl + '/' + id, this.httpOptions)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  private getCreatedJobProcessId(response: AddJobProcessResponse | null): string {
    return response?.data ?? response?.jobProcessId ?? EMPTY_GUID;
  }

  private handleError(err: HttpErrorResponse) {
    const errorMessage = `An error has occurred: ${err.error?.message ?? err.message}`;
    console.error(errorMessage);
    return throwError(() => errorMessage);
  }
}
